import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import theme, { tokens } from './theme';
import { CssBaseline, Container, CircularProgress, Box } from '@mui/material';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import { AuthProvider } from './context/AuthContext';
import { RequireTierPage } from './components/Gate';
import { registerWebMcpTools } from './webmcp';

// Lazy load pages for better performance
const FMRLookup = lazy(() => import('./pages/FMRLookup'));
const ZipCodePage = lazy(() => import('./pages/ZipCodePage'));
const CityPage = lazy(() => import('./pages/CityPage'));
const CitiesPage = lazy(() => import('./pages/CitiesPage'));
const HeatmapPage = lazy(() => import('./pages/HeatmapPage'));
const StatesPage = lazy(() => import('./pages/StatesPage'));
const StatePage = lazy(() => import('./pages/StatePage'));
const CountyPage = lazy(() => import('./pages/CountyPage'));
const GlossaryPage = lazy(() => import('./pages/GlossaryPage'));
const GlossaryTermPage = lazy(() => import('./pages/GlossaryTermPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));
const ChromeExtensionPage = lazy(() => import('./pages/ChromeExtensionPage'));
const ExpiredListingsPage = lazy(() => import('./pages/ExpiredListingsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));

// Report pages (lazy-loaded)
const ReportsIndex = lazy(() => import('./pages/reports/ReportsIndex'));
const BestStatesSection8 = lazy(() => import('./pages/reports/BestStatesSection8'));
const HighestFmrStates = lazy(() => import('./pages/reports/HighestFmrStates'));
const RentGrowthLeaders = lazy(() => import('./pages/reports/RentGrowthLeaders'));
const TopCashflowZips = lazy(() => import('./pages/reports/TopCashflowZips'));
const CheapestEntry = lazy(() => import('./pages/reports/CheapestEntry'));
const Section8Demand = lazy(() => import('./pages/reports/Section8Demand'));
const RenterHeavy = lazy(() => import('./pages/reports/RenterHeavy'));
const OccupancyStress = lazy(() => import('./pages/reports/OccupancyStress'));
const RentStability = lazy(() => import('./pages/reports/RentStability'));
const FmrZhviDivergence = lazy(() => import('./pages/reports/FmrZhviDivergence'));
const CashOnCash = lazy(() => import('./pages/reports/CashOnCash'));
const GradeUpgraders = lazy(() => import('./pages/reports/GradeUpgraders'));
const NewFmrAlert = lazy(() => import('./pages/reports/NewFmrAlert'));
const DollarStretch = lazy(() => import('./pages/reports/DollarStretch'));
const BedroomMixOptimizer = lazy(() => import('./pages/reports/BedroomMixOptimizer'));
const MetroArbitrage = lazy(() => import('./pages/reports/MetroArbitrage'));
const Explorer = lazy(() => import('./pages/reports/Explorer'));
const InvestmentMapUS = lazy(() => import('./pages/reports/InvestmentMapUS'));
const MapPage = lazy(() => import('./pages/MapPage'));
const UtilityAllowanceReport = lazy(() => import('./pages/reports/UtilityAllowanceReport'));
const BedroomPremium = lazy(() => import('./pages/reports/BedroomPremium'));
const SafmrVsMetroDelta = lazy(() => import('./pages/reports/SafmrVsMetroDelta'));

// Pro / billing
const ProSignup = lazy(() => import('./pages/ProSignup'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));

// Calculator pages
const CashFlowCalculator = lazy(() => import('./pages/calculators/CashFlowCalculator'));
const PurchasePriceCalculator = lazy(() => import('./pages/calculators/PurchasePriceCalculator'));
const BreakEvenCalculator = lazy(() => import('./pages/calculators/BreakEvenCalculator'));

// SEOContentManager removed - no longer needed since backend doesn't inject SEO content anymore

// Loading component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '60vh'
    }}
  >
    <CircularProgress />
  </Box>
);

function App() {
  useEffect(() => { registerWebMcpTools(); }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
        <div className="App">
          <Header />
          <Container maxWidth={false} sx={{ maxWidth: tokens.container, mt: { xs: 2, md: 3 }, mb: 4, px: { xs: 2, md: 3 } }}>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<FMRLookup />} />
                <Route path="/zipcode/:zipCode" element={<ZipCodePage />} />
                <Route path="/zip/:zipCode" element={<ZipCodePage />} />
                <Route path="/cities" element={<CitiesPage />} />
                <Route path="/city/:cityName/:state" element={<CityPage />} />
                <Route path="/states" element={<StatesPage />} />
                <Route path="/state/:stateCode" element={<StatePage />} />
                <Route path="/county/:countyName/:stateCode" element={<CountyPage />} />
                <Route path="/glossary" element={<GlossaryPage />} />
                <Route path="/glossary/:termSlug" element={<GlossaryTermPage />} />
                <Route path="/heatmap" element={<RequireTierPage title="Section 8 Investment Heatmap"><HeatmapPage /></RequireTierPage>} />

                {/* Reports */}
                <Route path="/reports" element={<ReportsIndex />} />
                <Route path="/best-states-section-8" element={<RequireTierPage title="Best States for Section 8"><BestStatesSection8 /></RequireTierPage>} />
                <Route path="/highest-fmr-states" element={<RequireTierPage title="Highest FMR States"><HighestFmrStates /></RequireTierPage>} />
                <Route path="/rent-growth-leaders" element={<RequireTierPage title="Rent Growth Leaders"><RentGrowthLeaders /></RequireTierPage>} />
                <Route path="/top-cashflow-zips" element={<RequireTierPage title="Top Cash Flow ZIPs"><TopCashflowZips /></RequireTierPage>} />
                <Route path="/cheapest-entry-zips" element={<RequireTierPage title="Cheapest Entry ZIPs"><CheapestEntry /></RequireTierPage>} />
                <Route path="/section-8-demand" element={<RequireTierPage title="Section 8 Demand"><Section8Demand /></RequireTierPage>} />
                <Route path="/renter-heavy-markets" element={<RequireTierPage title="Renter-Heavy Markets"><RenterHeavy /></RequireTierPage>} />
                <Route path="/occupancy-stress" element={<RequireTierPage title="Occupancy Stress"><OccupancyStress /></RequireTierPage>} />
                <Route path="/rent-stability" element={<RequireTierPage title="Rent Stability"><RentStability /></RequireTierPage>} />
                <Route path="/fmr-zhvi-divergence" element={<RequireTierPage title="FMR vs Home Value Divergence"><FmrZhviDivergence /></RequireTierPage>} />
                <Route path="/cash-on-cash" element={<RequireTierPage title="Cash-on-Cash Rankings"><CashOnCash /></RequireTierPage>} />
                <Route path="/grade-upgraders" element={<RequireTierPage title="Grade Upgraders"><GradeUpgraders /></RequireTierPage>} />
                <Route path="/new-fmr-alert" element={<RequireTierPage title="New FMR Alert"><NewFmrAlert /></RequireTierPage>} />
                <Route path="/dollar-stretch" element={<RequireTierPage title="Dollar Stretch"><DollarStretch /></RequireTierPage>} />
                <Route path="/bedroom-mix-optimizer" element={<RequireTierPage title="Bedroom Mix Optimizer"><BedroomMixOptimizer /></RequireTierPage>} />
                <Route path="/metro-arbitrage" element={<RequireTierPage title="Metro Arbitrage"><MetroArbitrage /></RequireTierPage>} />
                <Route path="/explorer" element={<RequireTierPage title="Market Explorer"><Explorer /></RequireTierPage>} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/map/state-scores" element={<RequireTierPage title="US Investment Map"><InvestmentMapUS /></RequireTierPage>} />
                <Route path="/utility-allowance-report" element={<RequireTierPage title="Utility Allowance Report"><UtilityAllowanceReport /></RequireTierPage>} />
                <Route path="/bedroom-premium" element={<RequireTierPage title="Bedroom Premium"><BedroomPremium /></RequireTierPage>} />
                <Route path="/safmr-vs-metro" element={<RequireTierPage title="SAFMR vs Metro FMR"><SafmrVsMetroDelta /></RequireTierPage>} />

                {/* Pro membership */}
                <Route path="/pro" element={<RequireTierPage title="fmr8 Pro"><ProSignup /></RequireTierPage>} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/account" element={<AccountPage />} />

                {/* Calculators */}
                <Route path="/calculator/cash-flow" element={<RequireTierPage title="Cash Flow Calculator"><CashFlowCalculator /></RequireTierPage>} />
                <Route path="/calculator/purchase-price" element={<RequireTierPage title="Purchase Price Calculator"><PurchasePriceCalculator /></RequireTierPage>} />
                <Route path="/calculator/break-even" element={<RequireTierPage title="Break-Even Calculator"><BreakEvenCalculator /></RequireTierPage>} />

                <Route path="/about" element={<AboutPage />} />
                <Route path="/methodology" element={<MethodologyPage />} />
                <Route path="/chrome-extension" element={<ChromeExtensionPage />} />
                <Route path="/expired-listings" element={<ExpiredListingsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />

                <Route path="/admin" element={<AdminPanel />} />
                {/* Catch-all route - redirect unknown routes to home page */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </Container>
          <Footer />
        </div>
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
